import React, { useState, useEffect } from 'react';
import { FaCompressArrowsAlt, FaExpandArrowsAlt } from 'react-icons/fa';
import JobCard from '../../../results/display/cards/JobCard';
import navigationService from '../../../../services/NavigationService';
import { useJobCardCollapse } from '../../../../hooks/useJobCardCollapse';
import '../styles/companion-views.css';

/**
 * CompanionJobList - Displays a list of jobs related to the current evidence
 * Used when viewing single job results to show other jobs from the same evidence
 */
const CompanionJobList = ({ data = {} }) => {
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(data.currentJobId || null);
  const [filter, setFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [targetFilter, setTargetFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [otherFiltersExpanded, setOtherFiltersExpanded] = useState(false);

  useEffect(() => {
    // Set jobs from data
    if (data.jobs) {
      setJobs(data.jobs);
    }
  }, [data]);

  // Get unique providers and targets
  const providerOptions = React.useMemo(() => {
    const set = new Set();
    jobs.forEach(job => {
      if (job.provider) {
        set.add(job.provider.toLowerCase());
      }
    });
    return Array.from(set).filter(Boolean);
  }, [jobs]);

  const targetOptions = React.useMemo(() => {
    const set = new Set();
    jobs.forEach(job => {
      const targets = job.analysisRequest?.targets || job.selectedTargets || job.selectedServices || [];
      targets.forEach(t => set.add(t));
    });
    return Array.from(set);
  }, [jobs]);

  // Filter jobs based on status, provider, and target
  const filteredJobs = React.useMemo(() => {
    let list = jobs.filter(job => {
      // Status filter
      if (filter === 'completed' && job.status !== 'completed') return false;
      if (filter === 'processing' && job.status !== 'processing') return false;
      if (filter === 'failed' && job.status !== 'failed') return false;
      
      // Provider filter
      if (providerFilter !== 'all' && (!job.provider || !job.provider.toLowerCase().includes(providerFilter))) {
        return false;
      }
      
      // Target filter
      if (targetFilter !== 'all') {
        const targets = job.analysisRequest?.targets || job.selectedTargets || job.selectedServices || [];
        if (!targets.includes(targetFilter)) return false;
      }
      
      return true;
    });

    // Sort jobs
    list.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      const costA = a.actualCost || 0;
      const costB = b.actualCost || 0;
      
      switch (sortBy) {
        case 'date_asc': return dateA - dateB;
        case 'cost_desc': return costB - costA;
        case 'cost_asc': return costA - costB;
        case 'date_desc':
        default: return dateB - dateA;
      }
    });

    return list;
  }, [jobs, filter, providerFilter, targetFilter, sortBy]);

  // Use collapse hook for managing job card states
  const {
    handleCollapseChange,
    areAllCollapsed,
    toggleAllCollapse,
    isJobCollapsed
  } = useJobCardCollapse(filteredJobs);

  // Group jobs by status for better organization (using all jobs, not filtered)
  const groupedJobs = React.useMemo(() => ({
    processing: jobs.filter(j => j.status === 'processing'),
    completed: jobs.filter(j => j.status === 'completed'),
    failed: jobs.filter(j => j.status === 'failed'),
  }), [jobs]);

  const handleJobClick = (jobId) => {
    // Find the job data to get the orderId
    const job = jobs.find(j => j.id === jobId);
    if (!job) {
      console.error('Job not found:', jobId);
      return;
    }

    // Use NavigationService to navigate to the job in the results modal
    const success = navigationService.navigateToJob(jobId, job.orderId, {
      updateUrl: true,
      source: 'companion_modal_job_click'
    });

    if (success) {
      console.log('✅ Navigated to job:', jobId, 'in order:', job.orderId);
      setSelectedJobId(jobId);
      
      // Still call the original callback if provided for compatibility
      if (data.onJobSelect) {
        data.onJobSelect(jobId);
      }
    } else {
      console.error('❌ Failed to navigate to job:', jobId);
    }
  };


  return (
    <div className="companion-job-list">

      {/* Filter Tabs - More compact */}
      <div className="companion-filter-tabs">
        <div className="filter-tabs-left">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({jobs.length})
          </button>
          <button
            className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed ({groupedJobs.completed.length})
          </button>
          <button
            className={`filter-tab ${filter === 'processing' ? 'active' : ''}`}
            onClick={() => setFilter('processing')}
          >
            Processing ({groupedJobs.processing.length})
          </button>
          {groupedJobs.failed.length > 0 && (
            <button
              className={`filter-tab ${filter === 'failed' ? 'active' : ''}`}
              onClick={() => setFilter('failed')}
            >
              Failed ({groupedJobs.failed.length})
            </button>
          )}
        </div>
        
        {/* Collapse All Button */}
        {filteredJobs.length > 0 && (
          <div className="filter-tabs-right">
            <button 
              className="collapse-all-btn"
              onClick={toggleAllCollapse}
              title={areAllCollapsed ? "Expand all job cards" : "Collapse all job cards"}
            >
              {areAllCollapsed ? (
                <>
                  <FaExpandArrowsAlt />
                  <span>Expand All</span>
                </>
              ) : (
                <>
                  <FaCompressArrowsAlt />
                  <span>Collapse All</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Other Filters - Collapsible */}
      <div className="companion-other-filters">
        <button 
          className="other-filters-toggle"
          onClick={() => setOtherFiltersExpanded(!otherFiltersExpanded)}
          aria-expanded={otherFiltersExpanded}
          aria-controls="other-filters-controls"
        >
          <span className="toggle-icon">{otherFiltersExpanded ? '▼' : '▶'}</span>
          <span className="toggle-text">Other Jobs</span>
          {!otherFiltersExpanded && (
            <span className="filter-summary">
              {providerFilter !== 'all' || targetFilter !== 'all' 
                ? '•' 
                : ''} {sortBy === 'date_desc' ? 'Newest' : sortBy === 'date_asc' ? 'Oldest' : 'Custom'}
            </span>
          )}
        </button>
        
        {otherFiltersExpanded && (
          <div 
            id="other-filters-controls"
            className="other-filters-content"
          >
            <div className="filter-group-compact">
              <label className="filter-label-compact">
                Provider
                <select 
                  value={providerFilter} 
                  onChange={(e)=>setProviderFilter(e.target.value)} 
                  className="filter-select-compact"
                >
                  <option value="all">All</option>
                  {providerOptions.map(opt => (
                    <option key={opt} value={opt}>{opt.replaceAll('-', ' ')}</option>
                  ))}
                </select>
              </label>
            </div>
            
            <div className="filter-group-compact">
              <label className="filter-label-compact">
                Target
                <select 
                  value={targetFilter} 
                  onChange={(e)=>setTargetFilter(e.target.value)} 
                  className="filter-select-compact"
                >
                  <option value="all">All</option>
                  {targetOptions.map(t => (
                    <option key={t} value={t}>{t.replaceAll('_', ' ')}</option>
                  ))}
                </select>
              </label>
            </div>
            
            <div className="filter-group-compact">
              <label className="filter-label-compact">
                Sort
                <select 
                  value={sortBy} 
                  onChange={(e)=>setSortBy(e.target.value)} 
                  className="filter-select-compact"
                >
                  <option value="date_desc">Newest</option>
                  <option value="date_asc">Oldest</option>
                  <option value="cost_desc">Cost (high→low)</option>
                  <option value="cost_asc">Cost (low→high)</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Jobs List */}
      <div className="companion-jobs-container">
        {filteredJobs.length === 0 ? (
          <div className="companion-no-results">
            <p>No jobs found</p>
          </div>
        ) : (
          <div className="companion-jobs-list">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className={`companion-job-wrapper ${selectedJobId === job.id ? 'selected' : ''}`}
              >
                <JobCard
                  job={job}
                  resultsMeta={{}}
                  size="regular"
                  onOpen={() => handleJobClick(job.id)}
                  collapsible={true}
                  isCollapsed={isJobCollapsed(job.id)}
                  onCollapseChange={(isCollapsed) => handleCollapseChange(job.id, isCollapsed)}
                />
                {selectedJobId === job.id && (
                  <div className="current-badge-overlay">
                    <span className="current-badge">Current</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default CompanionJobList;
